import React from 'react'
import { tokens } from "../../theme";
import { Box, Button, IconButton, useTheme, Modal, Fade, TextField, MenuItem, ListItemText, Radio, RadioGroup, FormControlLabel, FormLabel } from "@mui/material";
import CompetitionCard from "./CompetitionCard";
import Header from "../../components/Header";
import Loading from '../../components/Loading';
import Axios from 'axios';
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import { motion, AnimatePresence } from "framer-motion";
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import Alert from '../../components/alert/Alert';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import { getAuthHeader } from '../../utils/authToken';

const Competitions = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // for subject categories
    const subjectCategories = [
        "Biology",
        "Chemistry",
        "Physics",
        "Computer Science",
        "Economics",
        "English",
        "History",
        "Math",
        "Research",
        "Writing Competition",
        "Test Prep",
        "Social",
        "Camp",
    ];

    const [selectedSubject, setSelectedSubject] = React.useState([]);
    const handleSubjectChange = (event) => {
        const { target: { value } } = event;
        setSelectedSubject(typeof value === 'string' ? value.split(',') : value);
    };

    const [targetSubjects, setTargetSubjects] = React.useState([]);
    const [competitionType, setCompetitionType] = React.useState('competition');

    // Date range filters
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [selectedEventTypes, setSelectedEventTypes] = React.useState([]);
    const [filterCompetitionType, setFilterCompetitionType] = React.useState('');

    const handleSelectSubjects = (event) => {
        const { target: { value } } = event;
        setTargetSubjects(typeof value === 'string' ? value.split(',') : value);
    };

    const handleEventTypesChange = (event) => {
        const { target: { value } } = event;
        setSelectedEventTypes(typeof value === 'string' ? value.split(',') : value);
    };

    React.useEffect(() => {
        fetchCompetitions();
    }, [targetSubjects, filterCompetitionType, selectedEventTypes, startDate, endDate]);

    const style = {
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "20%",
        height: "35%",
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        p: 4,
    };

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);    
    
    const [loading, setLoading] = React.useState(true);
    const [competitions, setCompetitions] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [competitionUpdated, setCompetitionUpdated] = React.useState(false);
    const [competitionName, setCompetitionName] = React.useState("");
    const [competitionUrl, setCompetitionUrl] = React.useState("");

    async function fetchCompetitions() {
        const params = {};

        // Add filters only if they have values
        if (targetSubjects.length > 0) params.subjects = targetSubjects;
        if (filterCompetitionType) params.competition_type = filterCompetitionType;
        if (selectedEventTypes.length > 0) params.event_types = selectedEventTypes;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        await Axios.get(`${process.env.REACT_APP_URL}/api/v1/competitions`, {
            params,
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(async (response) => {
                setCompetitions(response.data);
                setLoading(false);
            }).catch((error) => {
                if (error.response?.status === 401)
                    handleLogout();
                else if (error.response?.status !== 404)
                    console.log(error.response?.data?.message);
                setLoading(false);
            });
        setCompetitionUpdated(false);
    };

    async function saveCompetitionInfo() {
        if (!competitionName) {
            setErrorAlertOpen(true);
            setErrorAlertMessage("Title is not provided!");
            return;
        }

        handleClose();

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/competitions`, {
            name: competitionName,
            url: competitionUrl,
            subject: selectedSubject,
            competition_type: competitionType,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((response) => {
            setSuccessAlertOpen(true);
            setSuccessAlertMessage(response.data.message);
            setCompetitionUpdated(true);
        }).catch((error) => {
            setErrorAlertOpen(true);
            setErrorAlertMessage(error.response?.data?.error || "Something went wrong");
        });
    };

    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Competitions');
        fetchCompetitions();

    }, [competitionUpdated]);

    const handleCompetitionNameChange = (e) => {
        setCompetitionName(e.target.value);
    };

    const handleUrlChange = (e) => {
        setCompetitionUrl(e.target.value);
    };

    return (
        <Box m="20px" marginX={3} marginY={3} >
            <Header title="Competitions" subtitle="Competitions for calendar" />
            { loading ? 
                <Loading />
            : <>
                <Box display="flex" flexDirection="column" gap={2} mb={2}>
                    {/* First Row: Search, Subjects, Add Button */}
                    <Box display="flex" justifyContent="left" gap={2} p={0}>
                        {/* SEARCH BAR */}
                        <Box
                            display="flex"
                            backgroundColor={colors.primary[600]}
                            borderRadius="3px"
                            boxShadow={10}
                            maxWidth={300}
                            minWidth={220}
                        >
                            <InputBase sx={{ ml: 2, flex: 1 }} onChange={(e) => setSearch(e.target.value)} placeholder="Search by competition title" />
                            <IconButton type="button" sx={{ p: 1 }}>
                                <SearchIcon />
                            </IconButton>
                        </Box>

                        {/* Subject Filter */}
                        <FormControl sx={{minWidth: '150px'}}>
                            <InputLabel id="subject-availability-label">Subjects</InputLabel>
                            <Select
                                labelId="subject-availability-label"
                                id="subject-availability"
                                multiple
                                value={targetSubjects}
                                onChange={handleSelectSubjects}
                                renderValue={(selected) => selected.join(', ')}
                                label="Subjects"
                            >
                                {subjectCategories.map((subject, index) => (
                                    <MenuItem key={index} value={subject}>
                                        <Checkbox checked={targetSubjects.indexOf(subject) > -1} />
                                        <ListItemText primary={subject} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Competition Type Filter */}
                        <FormControl sx={{minWidth: '150px'}}>
                            <InputLabel id="type-filter-label">Type</InputLabel>
                            <Select
                                labelId="type-filter-label"
                                id="type-filter"
                                value={filterCompetitionType}
                                onChange={(e) => setFilterCompetitionType(e.target.value)}
                                label="Type"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="competition">Competition</MenuItem>
                                <MenuItem value="test">Test</MenuItem>
                            </Select>
                        </FormControl>

                        <Box
                            display="flex"
                            backgroundColor={colors.primary[600]}
                            borderRadius="3px"
                            boxShadow={10}
                            maxWidth={300}
                            minWidth={220}
                        >
                            <Button
                                sx={{flex: 1 }}
                                variant="contained"
                                color="primary"
                                onClick={handleOpen}
                            >
                                Add Competition
                            </Button>
                        </Box>
                    </Box>

                    {/* Second Row: Date Range and Event Types */}
                    <Box display="flex" justifyContent="left" gap={2} p={0}>
                        {/* Start Date */}
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: '180px' }}
                        />

                        {/* End Date */}
                        <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: '180px' }}
                        />

                        {/* Event Types Filter */}
                        <FormControl sx={{minWidth: '200px'}}>
                            <InputLabel id="event-types-label">Event Types</InputLabel>
                            <Select
                                labelId="event-types-label"
                                id="event-types"
                                multiple
                                value={selectedEventTypes}
                                onChange={handleEventTypesChange}
                                renderValue={(selected) => {
                                    const labels = {
                                        'open': 'Open',
                                        'registration_deadline': 'Registration Deadline',
                                        'submission_deadline': 'Submission Deadline',
                                        'test': 'Test',
                                        'winner_announcement': 'Winner Announcement'
                                    };
                                    return selected.map(s => labels[s] || s).join(', ');
                                }}
                                label="Event Types"
                            >
                                <MenuItem value="open">
                                    <Checkbox checked={selectedEventTypes.indexOf('open') > -1} />
                                    <ListItemText primary="Open" />
                                </MenuItem>
                                <MenuItem value="registration_deadline">
                                    <Checkbox checked={selectedEventTypes.indexOf('registration_deadline') > -1} />
                                    <ListItemText primary="Registration Deadline" />
                                </MenuItem>
                                <MenuItem value="submission_deadline">
                                    <Checkbox checked={selectedEventTypes.indexOf('submission_deadline') > -1} />
                                    <ListItemText primary="Submission Deadline" />
                                </MenuItem>
                                <MenuItem value="test">
                                    <Checkbox checked={selectedEventTypes.indexOf('test') > -1} />
                                    <ListItemText primary="Test" />
                                </MenuItem>
                                <MenuItem value="winner_announcement">
                                    <Checkbox checked={selectedEventTypes.indexOf('winner_announcement') > -1} />
                                    <ListItemText primary="Winner Announcement" />
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {/* Clear Filters Button */}
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setSelectedEventTypes([]);
                                setTargetSubjects([]);
                                setFilterCompetitionType('');
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Box>
                </Box>

                {/* Competition CARDS */}
                <motion.div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                        rowGap: "1rem",
                        columnGap: "1rem",
                    }}
                    layout
                >
                    <AnimatePresence>
                        {competitions
                            .filter((competition) => {
                                return search === ''
                                    ? competition
                                    : competition.name.toLowerCase().includes(search.toLowerCase())
                                        || competition.name.toLowerCase().includes(search.toLowerCase());
                            })
                        .map((competition) => {
                            return (
                                <CompetitionCard
                                    key={competition.id}
                                    competition={competition}
                                    competitionUpdated={competitionUpdated}
                                    setCompetitionUpdated={setCompetitionUpdated}
                                    setSuccessAlertOpen={setSuccessAlertOpen}
                                    setSuccessAlertMessage={setSuccessAlertMessage}
                                    setErrorAlertOpen={setErrorAlertOpen}
                                    setErrorAlertMessage={setErrorAlertMessage}                
                                />
                            );
                        })}
                    </AnimatePresence>
                </motion.div>
            </>}
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical="bottom"
                horizontal="left"
            />
            <Modal
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                open={open}
                onClose={handleClose}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Box 
                            sx={{ 
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 4,
                                width: "100%",
                            }}
                        >
                            {/* Name */}
                            <TextField
                                id="input-with-icon-textfield"
                                label="Title of the competition?"
                                variant="standard"
                                onChange={handleCompetitionNameChange}
                                fullWidth
                            />
                            {/* URL */}
                            <TextField                        
                                id="input-with-icon-textfield"
                                label="URL of the competition homepage?"
                                variant="standard"
                                onChange={handleUrlChange}
                                fullWidth
                            />
                            {/* Subject - Multi-select */}
                            <FormControl variant="filled" sx={{ width: '100%' }}>
                                <InputLabel id="subject">Subjects</InputLabel>
                                    <Select
                                        labelId="subject"
                                        id="subject"
                                        multiple
                                        value={selectedSubject}
                                        onChange={handleSubjectChange}
                                        renderValue={(selected) => selected.join(', ')}
                                    >
                                        {subjectCategories.map((subject, index) => (
                                            <MenuItem key={index} value={subject}>
                                                <Checkbox checked={selectedSubject.indexOf(subject) > -1} />
                                                <ListItemText primary={subject} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            {/* Competition Type */}
                            <FormControl component="fieldset" sx={{ width: '100%' }}>
                                <FormLabel component="legend">Competition Type</FormLabel>
                                <RadioGroup
                                    row
                                    value={competitionType}
                                    onChange={(e) => setCompetitionType(e.target.value)}
                                >
                                    <FormControlLabel value="competition" control={<Radio />} label="Competition" />
                                    <FormControlLabel value="test" control={<Radio />} label="Test" />
                                </RadioGroup>
                            </FormControl>
                            <Button
                                variant='contained'
                                onClick={saveCompetitionInfo}
                            >
                                Create
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};


export default Competitions;